import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatNumber, formatDate, formatWeekday, formatEuro } from "@/lib/utils";

export default function HistoriePage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterMonth, setFilterMonth] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const months = [
    { v: "all", l: "Alle Monate" }, { v: "1", l: "Januar" }, { v: "2", l: "Februar" },
    { v: "3", l: "Maerz" }, { v: "4", l: "April" }, { v: "5", l: "Mai" },
    { v: "6", l: "Juni" }, { v: "7", l: "Juli" }, { v: "8", l: "August" },
    { v: "9", l: "September" }, { v: "10", l: "Oktober" }, { v: "11", l: "November" },
    { v: "12", l: "Dezember" },
  ];

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterYear, filterMonth]);

  const loadEntries = async () => {
    setLoading(true);
    setExpanded(false);
    try {
      const params = { year: parseInt(filterYear) };
      if (filterMonth !== "all") params.month = parseInt(filterMonth);
      const res = await api.get("/entries", { params });
      setEntries(res.data);
    } catch {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/entries/${deleteId}`);
      setEntries((prev) => prev.filter((e) => e.id !== deleteId));
      toast.success("Eintrag geloescht");
    } catch {
      toast.error("Fehler beim Loeschen");
    }
    setDeleteId(null);
  };

  const totalPoints = entries.reduce((s, e) => s + e.total_points, 0);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="historie-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
          Historie
        </h1>
        <p className="text-zinc-600 text-sm mt-0.5">
          {entries.length} Eintraege | {formatNumber(totalPoints)} Punkte gesamt
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-3" data-testid="filters">
        <Select value={filterYear} onValueChange={setFilterYear}>
          <SelectTrigger className="w-28 bg-zinc-950/50 border-zinc-800 h-10" data-testid="filter-year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value="2026">2026</SelectItem>
            <SelectItem value="2025">2025</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger className="w-40 bg-zinc-950/50 border-zinc-800 h-10" data-testid="filter-month">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            {months.map((m) => (
              <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Entries List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-zinc-900/50 animate-pulse" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-zinc-600 text-sm">Keine Eintraege gefunden</p>
          <Button
            onClick={() => navigate("/heute")}
            className="mt-4 bg-white text-black hover:bg-zinc-200"
            data-testid="create-first-entry-btn"
          >
            Ersten Eintrag erstellen
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {(expanded ? entries : entries.slice(0, 7)).map((entry) => (
            <Card key={entry.id} className="bg-card border-border hover:border-zinc-700 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-600 w-8">{formatWeekday(entry.date)}</span>
                      <span className="text-sm text-zinc-300">{formatDate(entry.date)}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="font-mono text-lg text-white font-medium">
                        {formatNumber(entry.total_points)}
                      </span>
                      <span className="text-[10px] text-zinc-600">Pkt.</span>
                      <span className="text-[10px] text-sky-400/60 font-mono">
                        Z:{formatNumber(entry.time_points)}
                      </span>
                      <span className="text-[10px] text-emerald-400/60 font-mono">
                        V:{formatNumber(entry.sales_points)}
                      </span>
                      {(entry.sales_entries?.zusage > 0 || entry.auftragswert > 0) && (
                        <span className="text-[10px] text-emerald-400 font-mono bg-emerald-950/50 px-1.5 py-0.5">
                          {entry.sales_entries?.zusage || 0}x Auftrag {entry.auftragswert > 0 ? `| ${formatEuro(entry.auftragswert)}` : ''}
                        </span>
                      )}
                    </div>
                    {entry.notes && (
                      <p className="text-[10px] text-zinc-600 mt-1 truncate max-w-xs">{entry.notes}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-1 ml-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-zinc-600 hover:text-white"
                      onClick={() => navigate(`/heute/${entry.date}`)}
                      data-testid={`edit-entry-${entry.date}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-zinc-600 hover:text-red-400"
                      onClick={() => setDeleteId(entry.id)}
                      data-testid={`delete-entry-${entry.date}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {entries.length > 7 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full py-3 text-xs text-zinc-500 hover:text-zinc-300 border border-zinc-800/60 bg-zinc-950/50 transition-colors"
              data-testid="toggle-more-entries"
            >
              {expanded
                ? `Weitere Eintraege ausblenden (${entries.length - 7})`
                : `Weitere ${entries.length - 7} Eintraege anzeigen`}
            </button>
          )}
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Eintrag loeschen?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400">
            Dieser Eintrag wird unwiderruflich geloescht.
          </p>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteId(null)}
              className="border-zinc-800"
              data-testid="cancel-delete-btn"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/20"
              data-testid="confirm-delete-btn"
            >
              Loeschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
